'use client';

import Link from 'next/link';
import { Caveat } from 'next/font/google';
import { usePathname } from 'next/navigation';
import { Show, useAuth, useClerk } from '@clerk/nextjs';
import { NavbarAccountControls } from '@/components/layout/navbar-account-controls';
import { LogoWithSkin } from '@/components/layout/logo-with-skin';
import { LogoIcon } from '@/components/shared/logo-icon';
import { useCosmetics } from '@/lib/achievements/cosmetics-context';
import { LanguageSwitcher } from '@/components/shared/language-switcher';
import { Button, buttonVariants } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { isSuperAdminViewClient, isSubscriptionViewClient, isOrgAdminViewClient, hasFeatureFlagClient, hasPaidSubscriptionClient, isManagedOrgClient } from '@/lib/permissions/client';
import { useAppShell } from '@/components/layout/app-shell-context';
import { useNavAccentLoadingActive } from '@/components/layout/nav-accent-loading-context';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, LogOut, Menu, Shield, ShoppingBag, Trophy } from 'lucide-react';
import { useRTLUtils, useLanguage } from '@/lib/translation';
import { GlobeIcon } from '@/components/shared/icons';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { TicketsPopover } from '@/components/layout/tickets-popover';
import { SuperAdminNavShortcuts } from '@/components/layout/super-admin-nav-shortcuts';
import { NavbarLocalhostBadge } from '@/components/layout/navbar-localhost-badge';
import { useAppNavItems } from '@/hooks/use-app-nav-items';
import { preventMobileSheetOutsideDismiss } from '@/components/layout/clerk-portal-guard';
import { getMobilePageTitle } from '@/lib/navigation/mobile-page-title';
import { isNavActive, isNavDropdownActive } from '@/lib/navigation/is-nav-active';
import { useAppState } from '@/hooks/use-app-state';
import { Badge } from '@/components/ui/badge';

import { NavbarHeaderPillLink } from '@/components/layout/navbar-header-pill';
import {
  MobileShellTopBar,
  MobileShellTopBarTrailing,
  mobileShellTopBarMenuTriggerClassName,
  mobileShellTopBarTitleClassName,
} from '@/components/layout/mobile-shell-top-bar';
import { MobileAppSidePanel } from '@/components/layout/mobile-app-side-panel';
import { MobileAppSideSheetHeader } from '@/components/layout/mobile-app-side-sheet-header';
import { ReachCopilotTopbarButton } from '@/components/copilot/reach-copilot-topbar-button';
import { isMainLandingRoute } from '@/lib/navigation/is-main-landing-route';

const caveatByKindway = Caveat({
  subsets: ['latin'],
  weight: ['600', '700'],
});

export function Navbar() {
  const pathname = usePathname();
  const { t } = useTranslation();
  const { directionClass, textAlignClass, isRTL } = useRTLUtils();
  const { currentLanguage, changeLanguage, supportedLanguages } = useLanguage();
  const { orgRole, sessionClaims } = useAuth();
  const { signOut } = useClerk();
  const { activeLogoSkinId } = useCosmetics();
  const { variant: shellVariant, superAdminShellOverride } = useAppShell();
  const isSuperAdmin = isSuperAdminViewClient(orgRole, sessionClaims);
  const isSubscriptionView = isSubscriptionViewClient(orgRole, sessionClaims);
  const { data: appState } = useAppState();
  const pendingFeedbackApprovals = appState?.pendingFeedbackApprovals ?? 0;
  const showVotesPendingBadge = pendingFeedbackApprovals > 0;
  const hasNoStoreFeature = hasFeatureFlagClient(sessionClaims, 'no-store');
  const isManagedOrg = isManagedOrgClient(sessionClaims);
  const hasPaid = hasPaidSubscriptionClient(sessionClaims);
  const { navItems, usageTabColor } = useAppNavItems();
  const accentBarLoading = useNavAccentLoadingActive();
  const isLandingShell = pathname === '/' || pathname.startsWith('/landing-v');
  // Show language switcher always on localhost (development) for easier testing
  const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const shouldShowLanguageSwitcher = isLocalhost;

  /** SaaS header pills: subscription orgs, or super-admin forced navbar on a custom org. */
  const showSaasHeaderPills =
    !isManagedOrg || (isSuperAdmin && superAdminShellOverride === 'navbar');

  const showStoreNav =
    isOrgAdminViewClient(orgRole) &&
    (hasPaid || isSuperAdmin) &&
    !hasNoStoreFeature &&
    showSaasHeaderPills;

  const shouldShowItems = () => {
    return pathname !== '/onboarding' && pathname !== '/';
  };

  const mobilePageTitle = shouldShowItems()
    ? getMobilePageTitle(pathname ?? '', t)
    : null;

  const isActive = (href: string) => isNavActive(pathname ?? '', href);

  const isDropdownActive = (dropdownItems?: Array<{ href: string; label: string }>) =>
    isNavDropdownActive(pathname ?? '', dropdownItems);

  // Mobile menu: EN opens from left, HE stays on right
  const mobileSheetSide = isRTL ? 'right' : 'left';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const useCompactMobileAppBar = shouldShowItems() && !isLandingShell;

  if (shellVariant === 'custom' && !isMainLandingRoute(pathname)) {
    return null;
  }

  const renderMobileNavSheet = () => (
    <Sheet
      modal={false}
      open={isMobileMenuOpen}
      onOpenChange={setIsMobileMenuOpen}
    >
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-mobile-nav-trigger
          className={mobileShellTopBarMenuTriggerClassName}
          aria-label={t('navigation.openMenu')}
        >
          <Menu className="shrink-0" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side={mobileSheetSide}
        className={cn(
          directionClass,
          'flex h-full max-h-dvh flex-col gap-0 overflow-x-hidden p-0 w-[12.5rem] max-w-[58vw] sm:w-[15rem] sm:max-w-[17rem]',
          isRTL
            ? 'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right'
            : 'data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
        )}
        onInteractOutside={preventMobileSheetOutsideDismiss}
        onPointerDownOutside={preventMobileSheetOutsideDismiss}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <MobileAppSideSheetHeader theme="navbar" onClose={() => setIsMobileMenuOpen(false)} />
        <div className="min-h-0 flex-1 overflow-hidden">
          <MobileAppSidePanel
            theme="navbar"
            onNavigate={() => setIsMobileMenuOpen(false)}
          />
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <nav
      className={cn(
        'relative overflow-hidden ltr',
        useCompactMobileAppBar
          ? 'bg-white/90 backdrop-blur-sm dark:bg-emerald-950/78 md:bg-white md:dark:bg-gray-800/95'
          : 'bg-white dark:bg-gray-800/95',
      )}
    >
      {/* Emerald glow — desktop only; mobile app bar uses flat white like custom shell */}
      <div className="pointer-events-none absolute inset-y-[-64px] left-[-96px] hidden w-[28rem] bg-gradient-to-br from-emerald-300/90 via-emerald-200/40 to-transparent blur-3xl dark:from-emerald-600/18 dark:via-emerald-700/8 md:block" />
      <div className="pointer-events-none absolute inset-y-[-64px] right-[-96px] hidden w-[28rem] bg-gradient-to-bl from-emerald-300/90 via-emerald-200/40 to-transparent blur-3xl dark:from-emerald-600/18 dark:via-emerald-700/8 md:block" />
      <div className="relative z-10 w-full px-3 md:px-4">
        {shouldShowItems() && !isLandingShell ? (
          <div className="pointer-events-none absolute inset-x-0 top-1/2 z-20 hidden -translate-y-1/2 justify-center md:flex">
            <NavbarLocalhostBadge />
          </div>
        ) : null}
        <Show when="signed-in">
          {useCompactMobileAppBar ? (
            <MobileShellTopBar
              menu={renderMobileNavSheet()}
              title={
                <span className={mobileShellTopBarTitleClassName(textAlignClass)}>
                  {mobilePageTitle ?? 'Reach'}
                </span>
              }
              trailing={<MobileShellTopBarTrailing theme="navbar" />}
            />
          ) : null}
        </Show>
        <div
          className={cn(
            'items-center justify-between md:h-16',
            useCompactMobileAppBar ? 'hidden md:flex' : 'flex h-14 md:h-16',
            isLandingShell ? 'flex-row-reverse md:flex-row' : !isRTL ? 'flex-row-reverse' : '',
          )}
        >
          {/* Logo/Brand - hidden on mobile when home so auth section spans full bar */}
          <div
            className={cn(
              'flex items-center',
              isLandingShell ? 'hidden md:flex' : 'gap-4',
              !isLandingShell && currentLanguage.code === 'en' && 'md:flex-row-reverse',
            )}
          >
            {shouldShowItems() ? (
              isLandingShell ? (
                <div className="hidden md:flex select-none items-center gap-3">
                  <Link
                    href="/dashboard"
                    className="flex items-center gap-2 transition-opacity hover:opacity-90"
                    aria-label="Reach"
                  >
                    <div className="flex h-8 w-8 items-center justify-center">
                      <LogoIcon className="h-8 w-8" />
                    </div>
                    <span className="text-xl font-bold leading-tight text-gray-900 dark:text-white">
                      Reach
                    </span>
                  </Link>
                  <Link
                    href="https://kindway.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    dir="ltr"
                    className={cn(
                      caveatByKindway.className,
                      'inline-block text-[1.5rem] font-semibold leading-none text-sky-500 hover:text-sky-600 hover:underline dark:text-sky-400 dark:hover:text-sky-300',
                    )}
                  >
                    {t('navigation.brandByKindway')}
                  </Link>
                </div>
              ) : (
                <Link
                  href="/dashboard"
                  className={cn(
                    'hidden md:flex items-center select-none gap-2 hover:opacity-90 transition-opacity'
                  )}
                  aria-label="Reach"
                >
                  <div className="w-8 h-8 flex items-center justify-center">
                    <LogoWithSkin skinId={activeLogoSkinId} className="w-8 h-8" />
                  </div>
                  <div className="flex flex-col">
                    <span className="hidden md:block text-xl font-bold text-gray-900 dark:text-white leading-tight">
                      Reach
                    </span>
                  </div>
                </Link>
              )
            ) : isLandingShell ? (
              <div className="hidden md:flex select-none items-center gap-3" aria-label="Reach">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center">
                    <LogoIcon className="h-8 w-8" />
                  </div>
                  <span className="text-xl font-bold leading-tight text-gray-900 dark:text-white">
                    Reach
                  </span>
                </div>
                <Link
                  href="https://kindway.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  dir="ltr"
                  className={cn(
                    caveatByKindway.className,
                    'inline-block text-[1.875rem] font-semibold leading-none text-sky-500 hover:text-sky-600 hover:underline dark:text-sky-400 dark:hover:text-sky-300',
                  )}
                >
                  {t('navigation.brandByKindway')}
                </Link>
              </div>
            ) : (
              <div
                className={cn(
                  'hidden md:flex items-center select-none gap-2'
                )}
                aria-label="Reach"
              >
                <div className="w-8 h-8 flex items-center justify-center">
                  <LogoWithSkin skinId={activeLogoSkinId} className="w-8 h-8" />
                </div>
                <div className="flex flex-col">
                  <span className="hidden md:block text-xl font-bold text-gray-900 dark:text-white leading-tight">
                    Reach
                  </span>
                </div>
              </div>
            )}

            <Show when="signed-in">
              {isLandingShell && shouldShowLanguageSwitcher ? (
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 w-9 rounded-full border border-yellow-200 dark:border-yellow-900/50 p-0 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 dark:text-yellow-300 dark:hover:text-yellow-200 dark:hover:bg-yellow-900/20"
                      aria-label={t('language.switchLanguage', 'Switch Language')}
                    >
                      <GlobeIcon size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="min-w-[140px]">
                    {supportedLanguages.map((language) => (
                      <DropdownMenuItem
                        key={language.code}
                        onClick={() => {
                          changeLanguage(language.code);
                          if (typeof window !== 'undefined') {
                            window.location.reload();
                          }
                        }}
                        className={`cursor-pointer ${directionClass}`}
                      >
                        <span className={`${textAlignClass}`}>{language.name}</span>
                        {currentLanguage.code === language.code && (
                          <div className={cn('ml-auto w-2 h-2 bg-primary rounded-full', isRTL && 'ml-0 mr-auto')} />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </Show>

            {/* Support/Tickets button – fixed near logo on the left side */}
            <Show when="signed-in">
              {shouldShowItems() && (
                <div className="flex items-center gap-2">
                  {isRTL ? <ReachCopilotTopbarButton /> : null}
                  <div
                    dir={isRTL ? 'rtl' : 'ltr'}
                    className="hidden items-center gap-2 md:flex"
                  >
                    {showStoreNav ? (
                      <NavbarHeaderPillLink
                        href="/reachstore"
                        label={t('navigation.store')}
                        ariaLabel={t('navigation.store')}
                        icon={<ShoppingBag className="h-4 w-4" aria-hidden />}
                        active={isActive('/reachstore')}
                      />
                    ) : null}
                    <TooltipProvider delayDuration={0}>
                      <TicketsPopover isSuperAdmin={!!isSuperAdmin} isActive={isActive('/tickets')} />
                    </TooltipProvider>
                  </div>
                  {!isRTL ? <ReachCopilotTopbarButton /> : null}
                  {isSuperAdmin ? (
                    <Link
                      href="/admin"
                      aria-label={t('navigation.admin', 'Admin')}
                      className={cn(
                        buttonVariants({ variant: 'outline', size: 'sm' }),
                        'hidden md:inline-flex h-8 px-3 gap-2 text-yellow-700 hover:text-yellow-800 border-yellow-200 hover:bg-yellow-50 dark:text-yellow-300 dark:hover:text-yellow-200 dark:border-yellow-900/50 dark:hover:bg-yellow-900/20',
                        isActive('/admin') && 'bg-yellow-50 dark:bg-yellow-900/20'
                      )}
                    >
                      <Shield className="h-4 w-4" />
                      <span className="text-sm">{t('navigation.admin', 'Admin')}</span>
                    </Link>
                  ) : null}
                  {shouldShowLanguageSwitcher || isSuperAdmin ? (
                    <SuperAdminNavShortcuts
                      theme="product"
                      showAdminLinks={!!isSuperAdmin}
                    />
                  ) : null}
                </div>
              )}
            </Show>
          </div>

          {/* Mobile page title (uses existing nav translations) - hidden on home when signed out so auth spans full bar */}
          <div className={cn('md:hidden flex-1 px-2', isLandingShell && 'hidden')}>
            {mobilePageTitle ? (
              <div className={cn('text-lg font-semibold text-gray-800 dark:text-gray-100 truncate', textAlignClass)}>
                {mobilePageTitle}
              </div>
            ) : null}
          </div>

          {/* Navigation Links + auth controls - flex-1 only when SignedOut on home so auth spans full bar */}
          <div className={cn(
            'flex items-center',
            isLandingShell ? 'flex-1 min-w-0' : '',
            isRTL ? 'space-x-1.5' : isLandingShell ? 'gap-1.5' : 'gap-1.5 flex-row-reverse',
          )}>
            {/* Logged out: keep CTAs visible on the main landing route only */}
            <Show when="signed-out">
              {isLandingShell && (
                <div className="flex items-center justify-between w-full md:hidden">
                  <div className="shrink-0">
                    <LanguageSwitcher />
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Link href="/sign-in">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 px-2.5 text-xs font-medium justify-center shrink-0"
                      >
                        {t('navigation.signIn')}
                      </Button>
                    </Link>
                    <Link href="/sign-up">
                      <Button
                        size="sm"
                        className="h-9 px-2.5 text-xs font-medium justify-center shrink-0 bg-green-600 hover:bg-green-700 text-white"
                      >
                        {t('navigation.signUp')}
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </Show>
            <Show when="signed-in">
              {isLandingShell && (
                <div className="flex items-center justify-between w-full md:hidden">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0 hover:bg-transparent dark:hover:bg-transparent"
                    aria-label={t('navigation.signOut')}
                    onClick={() => {
                      void signOut({ redirectUrl: '/' });
                    }}
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                  <Link href="/dashboard" className="shrink-0">
                    <Button size="sm" className="h-9 bg-green-600 hover:bg-green-700 text-white">
                      {t('navigation.toTheApp')}
                    </Button>
                  </Link>
                </div>
              )}
            </Show>

            <Show when="signed-in">
              {shouldShowItems() && (
                <>
                  {/* Desktop tabs */}
                  <div className={`hidden md:flex items-center ${isRTL ? 'space-x-1' : 'gap-1 flex-row-reverse'}`}>
                  {/* For HE: use navItems as-is. For EN: use same order but reversed container mirrors HE */}
                  {(isRTL ? navItems : navItems).map((item) => {
                    const isBillingTab = item.href === '/billing';
                    const isActiveTab = isActive(item.href);
                    // Red styling for billing tab when expired/0 days left (only for subscription-level users on free trial)
                    // Yellow styling for billing tab when low days remaining (only for subscription-level users on free trial)
                    const shouldShowBillingColor = isBillingTab && isSubscriptionView && !hasPaid;
                    const isSuperAdminTab = Boolean(item.superAdminOnly);
                    const isYellowTab = shouldShowBillingColor && usageTabColor === 'yellow';
                    const isRedTab = shouldShowBillingColor && usageTabColor === 'red';

                    if (item.isDropdown && item.dropdownItems) {
                      const isActiveDropdown = isDropdownActive(item.dropdownItems);
                      const triggerClasses = `
                        px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 cursor-pointer
                        ${directionClass}
                        ${isActiveDropdown
                          ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                        }
                      `;
                      const triggerContent = (
                        <>
                          {item.label}
                          <ChevronDown className="w-4 h-4" />
                        </>
                      );
  
                      const votesInGroup = item.dropdownItems.some(
                        (dropdownItem) => dropdownItem.href === '/votes' || dropdownItem.href.startsWith('/votes?'),
                      );
                      return (
                        <DropdownMenu key={item.label} modal={false}>
                          {item.href && item.href !== '#' ? (
                            <DropdownMenuTrigger asChild>
                              <Link href={item.href} className={triggerClasses}>
                                {triggerContent}
                              </Link>
                            </DropdownMenuTrigger>
                          ) : (
                            <DropdownMenuTrigger className={`${triggerClasses} relative`}>
                              {votesInGroup && showVotesPendingBadge ? (
                                <Badge
                                  variant="outline"
                                  className="absolute -top-1 -end-1 h-5 min-w-5 shrink-0 rounded-md border-orange-200 bg-orange-100 px-1.5 py-0 text-[10px] font-semibold leading-none text-orange-800 tabular-nums dark:border-orange-700/80 dark:bg-orange-950/40 dark:text-orange-200"
                                >
                                  {pendingFeedbackApprovals > 99 ? '99+' : pendingFeedbackApprovals}
                                </Badge>
                              ) : null}
                              {triggerContent}
                            </DropdownMenuTrigger>
                          )}
                              <DropdownMenuContent className={`${directionClass}`}>
                            {item.dropdownItems.map((dropdownItem) => {
                              const isActiveDropdownItem = isActive(dropdownItem.href);
                              const isVotesChild = dropdownItem.href === '/votes' || dropdownItem.href.startsWith('/votes?');
                              const dropdownItemClasses = `
                                ${isActiveDropdownItem ? 'bg-green-50 dark:bg-green-900/20' : ''}
                              `;
                              return (
                                <DropdownMenuItem key={dropdownItem.href} asChild>
                                  <Link
                                    href={dropdownItem.href}
                                    className={dropdownItemClasses}
                                  >
                                    <span className="inline-flex w-full items-center justify-between gap-3">
                                      {dropdownItem.label}
                                      {isVotesChild && showVotesPendingBadge ? (
                                        <Badge
                                          variant="outline"
                                          className="h-5 min-w-5 shrink-0 rounded-md border-orange-200 bg-orange-100 px-1.5 py-0 text-[10px] font-semibold leading-none text-orange-800 tabular-nums dark:border-orange-700/80 dark:bg-orange-950/40 dark:text-orange-200"
                                        >
                                          {pendingFeedbackApprovals > 99 ? '99+' : pendingFeedbackApprovals}
                                        </Badge>
                                      ) : null}
                                    </span>
                                  </Link>
                                </DropdownMenuItem>
                              );
                            })}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      );
                    }
                    const inactiveClasses = isRedTab
                      ? 'text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/30'
                      : isSuperAdminTab
                      ? 'text-yellow-700 hover:bg-yellow-50 hover:text-yellow-800 dark:text-yellow-300 dark:hover:text-yellow-200 dark:hover:bg-yellow-900/20'
                      : isYellowTab
                      ? 'text-yellow-600 hover:bg-yellow-50 dark:text-yellow-300 dark:hover:bg-yellow-900/30'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700';
                    const activeClasses = isRedTab
                      ? 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                      : isSuperAdminTab
                      ? 'bg-yellow-50 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                      : isYellowTab
                      ? 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                      : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';

                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        className={`
                          px-4 py-2 rounded-lg text-sm font-medium transition-colors inline-flex relative items-center gap-1
                          ${isActiveTab ? activeClasses : inactiveClasses}
                        `}
                      >
                        {item.icon === 'achievements' && <Trophy className="w-3.5 h-3.5 text-amber-500" />}
                        {(item.href === '/votes' || item.href.startsWith('/votes?')) && showVotesPendingBadge ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Badge
                              variant="outline"
                              className="h-5 min-w-5 shrink-0 absolute -top-1 -end-1 rounded-md border-orange-200 bg-orange-100 px-1.5 py-0 text-[10px] font-semibold leading-none text-orange-800 tabular-nums dark:border-orange-700/80 dark:bg-orange-950/40 dark:text-orange-200"
                            >
                              {pendingFeedbackApprovals > 99 ? '99+' : pendingFeedbackApprovals}
                            </Badge>
                            <span className="min-w-0">{item.label}</span>

                          </span>
                        ) : (
                          item.label
                        )}
                      </Link>
                    );
                  })}
                  </div>
                </>
              )}
            </Show>

            {/* Auth Section (desktop only; mobile lives inside the menu sheet) */}
            <div className={`hidden md:flex md:ml-auto items-center gap-2 ${!isRTL ? 'flex-row-reverse' : ''}`}>
              <Show when="signed-out">
                {isLandingShell && isRTL ? <LanguageSwitcher /> : null}
                <div className="flex items-center gap-3">
                  <Link href="/sign-in">
                    <Button variant="outline" size="sm" className="h-9 w-[68px] justify-center border-gray-300 dark:border-gray-600">
                      {t('navigation.signIn')}
                    </Button>
                  </Link>
                  <Link href="/sign-up">
                    <Button size="sm" className="h-9 w-[68px] justify-center bg-green-600 hover:bg-green-700 text-white">
                      {t('navigation.signUp')}
                    </Button>
                  </Link>
                </div>
                {isLandingShell && !isRTL ? <LanguageSwitcher /> : null}
              </Show>
              <Show when="signed-in">
                {isRTL ? (
                  <NavbarAccountControls />
                ) : (
                  <>
                    {pathname === '/' && (
                      <Link href="/dashboard" className="mx-0 hidden md:inline-flex ml-2">
                        <Button variant="default" className="bg-green-600 hover:bg-green-700 text-white">
                          {t('navigation.toTheApp')}
                        </Button>
                      </Link>
                    )}
                    {/* For EN: Order reversed in DOM so flex-row-reverse shows user menu | org switcher visually */}
                    <NavbarAccountControls />
                  </>
                )}
              </Show>
            </div>

            <Show when="signed-in">
              {pathname === '/' && isRTL && (
                <Link href="/dashboard" className="mx-0 hidden md:inline-flex ml-2">
                  <Button variant="default" className="bg-green-600 hover:bg-green-700 text-white">
                    {t('navigation.toTheApp')}
                  </Button>
                </Link>
              )}
            </Show>
          </div>
        </div>
      </div>
      {/* Reach achievements cosmetic accent bar – theme-colored stripe at the bottom of the navbar */}
      <div
        aria-hidden
        className={cn(
          'reach-accent-bar absolute inset-x-0 bottom-0 h-[3px] opacity-70',
          accentBarLoading && 'reach-accent-bar--loading',
        )}
      />
    </nav>
  );
}

